import { useRef, useState } from 'react';

import { Flex, Select, TextArea } from '@raystack/apsara';

import { For, Show } from '@src/hoc';

import styles from './OrderDeskUseCase.module.css';

interface OrderDeskUseCase {
  onValueChange: React.ComponentProps<typeof Select>['onValueChange'];
  value: string;
  error: string;
}

export const USE_CASE_OPTIONS = {
  agriculture: 'Agriculture',
  forestry: 'Forestry',
  climate_and_biodiversity: 'Climate and Biodiversity',
  mineralogy_and_mining: 'Mineralogy and Mining',
  water_quality: 'Water Quality',
  energy: 'Energy',
  urban_monitoring: 'Urban Monitoring',
  other_than_above: 'Other than above'
};

export const OrderDeskUseCase = ({
  onValueChange,
  value,
  error
}: OrderDeskUseCase) => {
  const options = Object.entries(USE_CASE_OPTIONS).map(([key, label]) => ({
    id: key,
    label
  }));

  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isCustomUseCase, setIsCustomUseCase] = useState(
    !options.some(d => d.id === value)
  );

  function handleValueChange(value: string) {
    if (value !== options.at(-1)!['id']) {
      setIsCustomUseCase(false);
      onValueChange?.(value);
    } else {
      setIsCustomUseCase(true);
      contentRef.current?.scrollIntoView({ behavior: 'smooth' });
      onValueChange?.('');
    }
  }

  return (
    <Flex direction='column' className={styles.container}>
      <Select
        onValueChange={handleValueChange}
        value={isCustomUseCase ? options.at(-1)!['id'] : value}
      >
        <Select.Trigger>
          <Select.Value placeholder={'Provide a use case for this order'} />
        </Select.Trigger>

        <Select.Content>
          <Select.Group>
            <For each={options}>
              {option => (
                <Select.Item key={option.id} value={option.id}>
                  {option.label}
                </Select.Item>
              )}
            </For>
          </Select.Group>
        </Select.Content>
      </Select>

      <div ref={contentRef}>
        <Show when={isCustomUseCase}>
          <TextArea
            value={value}
            onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
              onValueChange?.(event.target.value)
            }
            helperText={error}
            data-test-id='custom-use-case'
          />
        </Show>
      </div>
    </Flex>
  );
};
